import { useLocation } from "react-router-dom";
import { Button, IconButton, useMediaQuery, useTheme } from "@mui/material";

import { useRouter } from "src/routes/hooks";

import { useTranslate } from "src/locales";

import Iconify from "src/components/iconify";
import { paths } from "src/routes/paths";

const RETURN_KEY = 'kb_return_to';

// Pop-out window size (capped to the current window), mirroring utils/web.utils.
const POPOUT_WIDTH = 900;
const POPOUT_HEIGHT = 900;

export const KnowledgeBaseButton = () => {

  const { t } = useTranslate();

  const theme = useTheme();

  const router = useRouter();

  const { pathname } = useLocation();

  const isNotLargeDevice = useMediaQuery(theme.breakpoints.down('md'));

  // Don't render a Help button while inside the reader itself (e.g. the pop-out window).
  if (pathname.startsWith(paths.help.root)) return null;

  const openKnowledgeBase = () => {
    // The app has no router basename (see main.tsx), so /help is a bare route that
    // resolves against the current origin in every environment — safe to open directly.
    const url = `${window.location.origin}${paths.help.root}`;

    const width = Math.min(POPOUT_WIDTH, window.innerWidth);
    const height = Math.min(POPOUT_HEIGHT, window.innerHeight);
    const left = (window.innerWidth - width) / 2 + window.screenX;
    const top = (window.innerHeight - height) / 2 + window.screenY;

    // Must be called synchronously in the click handler to avoid pop-up blockers.
    const popup = window.open(
      url,
      'jt_knowledge_base',
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},top=${top},left=${left}`
    );

    if (popup) {
      popup.focus();
      return;
    }

    // Pop-up blocked → fall back to in-app navigation, remembering where to return.
    try {
      const here = window.location.pathname + window.location.search;
      if (!here.startsWith(paths.help.root)) sessionStorage.setItem(RETURN_KEY, here);
    } catch {
      // sessionStorage unavailable; the reader's close button falls back to dashboard
    }
    router.push(paths.help.root);
  };

  if (isNotLargeDevice) {
    return (
      <IconButton color="primary" onClick={openKnowledgeBase}>
        <Iconify icon="solar:documents-line-duotone" />
      </IconButton>
    );
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      startIcon={<Iconify icon="solar:documents-line-duotone" />}
      onClick={openKnowledgeBase}
    >
      {t('common.help')}
    </Button>
  );
}
